import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import TextFieldComponent from '../../../components/TextFieldComponent';
import zod from 'zod';
import { Grid2 } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import ButtonComponent from '../../../components/ButtonComponent';
import { AppContext } from '../../../context/AppContextProvider';
import ModalComponent from '../../../components/ModalComponent';
import DropdownSelectComponent from '../../../components/DropdownSelectComponent';

import {
  useCreateSchoolTicketAsync, 
  useUpdateSchoolTicketAsync,
} from '../../../services/SchoolTicket/hooks/useMutation';
import { SchoolTicketViewSchema } from '../../../services/SchoolTicket/schemas';
import { useGetAllStudentsByLicenseMutateAsync } from '../../../services/Recognition/Student/hooks/useMutation';
import { IStudentRecognitionSimpleProps } from '../../../services/Recognition/Student/types';

export type SchoolTicketFormInput = zod.infer<typeof SchoolTicketViewSchema>;

interface SchoolTicketEditionModalProps {
  isOpen: boolean;
  responsibleEdit?: any;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  handleSaveForm?: () => void;
  handleClose?: () => void;
}

const SchoolTicketEditionModal = ({
  isOpen,
  setIsOpen,
  responsibleEdit,
  handleClose,
  handleSaveForm,
}: SchoolTicketEditionModalProps) => {
  const [isDeviceSelected, setIsDeviceSelected] = useState<boolean>(false);
  const form = useForm<SchoolTicketFormInput>({
    resolver: zodResolver(SchoolTicketViewSchema),
    defaultValues: {
      cpf: '',
      name: '',
    },
  });
  const {
    control,
    handleSubmit,
    reset,
  } = form;
  const { showSnackbar } = useContext(AppContext);

  const [students, setStudents] = useState<IStudentRecognitionSimpleProps[]>(
    [],
  );

  const {
    mutateAsync: mutateCreateSchoolTicket,
    isLoading: isCreating,
  } = useCreateSchoolTicketAsync();
  const { mutateAsync: mutateGetAllStudentByLicenseAsync } =
    useGetAllStudentsByLicenseMutateAsync();
  const { mutateAsync: mutateUpdateSchoolTicket, isLoading: isUpdating } =
    useUpdateSchoolTicketAsync();

  useEffect(() => {
    if (responsibleEdit) {
      reset(responsibleEdit, { keepDefaultValues: true });
    }
  }, [responsibleEdit]);

  const handleSave = (data: any) => {
    if (data.id) {
      mutateUpdateSchoolTicket(
        {
          data,
          id: data.id,
        },
        {
          onSuccess: () => {
            resetAllAndShowMessage(
              'Ticket escolar atualizado com sucesso.',
            );
          },
        },
      );
      return;
    }

    mutateCreateSchoolTicket(
      {
        data,
      },
      {
        onSuccess: () => {
          resetAllAndShowMessage('Ticket escolar cadastrado com sucesso.');
        },
        onError() {
          showSnackbar('Erro ao cadastrar Ticket escolar.', 'error');
        },
      },
    );
  };

  const resetAllAndShowMessage = (message: string) => {
    if (handleSaveForm) handleSaveForm();
    showSnackbar(message, 'success');
    reset();
    setIsOpen(false);
  };

  const onCloseModal = () => {
    setIsOpen(false);
    if (handleClose) handleClose();
    reset();
  };

  useEffect(() => {
    mutateGetAllStudentByLicenseAsync(undefined).then(setStudents);
  }, []);

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onCloseModal}
      title={'Edição de Ticket Escolar'}
    >
      <Grid2
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        sx={{ mt: 2 }}
      >
        <Grid2 size={isDeviceSelected ? 12 : 6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Nome"
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        
        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropdownSelectComponent
                label="Alunos"
                selectedValues={value as any[] || []}
                onSelectedValues={onChange}
                options={students}
                labelField={'name'}
                valueField={'id'}
              />
            )}
            name={'studentIds'} 
            control={control}
          />
        </Grid2>
      </Grid2>
      <ButtonComponent
        fullWidth
        sx={{ mt: 3 }}
        onClick={handleSubmit(handleSave)}
        loading={isCreating || isUpdating}
      >
        Salvar
      </ButtonComponent>
    </ModalComponent>
  );
};

export default SchoolTicketEditionModal;